import styled from 'styled-components/macro';
import { useCallback, useEffect } from 'react';
import { useRecoilValue } from 'recoil';
import { Flex } from 'rebass/styled-components';
import once from 'lodash/once';

import { rem } from '@blueprint/ui';

import { IconButton, Typography, Spinner } from 'components/blueprint';
import { useSafeLabel } from 'hooks';
import { hasAgentConversationStartedState } from 'state/hasAgentConversationStarted';
import { clearConversation } from 'genesys/genesys';
import { registerAuthPlugin } from 'genesys/registerAuthPlugin';
import { useSetGenesysError, DefinedError } from 'genesys/error/error-state';
import { useMessagingServiceStatus } from 'genesys/useMessagingServiceStatus';
import { useInteractionTracking } from 'tracking/hooks/useInteractionTracking';
import { breakpoints, colors } from 'themes';

import messages from './messages';
import { useOpenningHoursEffect } from './useOpenningHoursEffect';
import { AuthState, useAuthenticationState } from 'state/authenticationState';
import { useSetHasUserInitiatedAuthChat } from 'state/hasUserInitiatedAuthChat';
import { useSetHasChatWithAgentBtnClicked } from 'state/hasChatWithAgentBtnClicked';
import { isLoggedInUser } from 'utils/isLoggedInUser';
import { startAConversation } from 'hooks/useStartConversation';
import { waitForMessagingServiceStarted } from 'genesys/waitForMessagingServiceStarted';
import { oneColumnLayout } from 'themes';
import { publish } from 'pubsub-js';
import { PubSubEvent } from 'genesys/pub-sub-events';
import { useIsGenesysReAuthenticating } from 'hooks/useIsGenesysReAuthenticating';

const { black, white } = colors;

const ChatWithAnAgentButton = styled(IconButton)`
  && {
    background: ${black};
    border: ${rem(1)} solid;

    :hover,
    :focus {
      background: ${black};
      border: ${rem(1)} solid;
    }
  }

  /* stylelint-disable-next-line unit-allowed-list */
  @media (min-width: ${breakpoints.md}px) {
    && {
      width: ${rem(182)};
    }
  }

  /* stylelint-disable-next-line unit-allowed-list */
  @media (max-width: ${breakpoints.md - 1}px) {
    && {
      flex-grow: 1;
    }
  }
`;

/**
 * ChatWithUs
 * This component enable when more than
 * one conversational messages has started.
 * It also gives a user an option to start a
 * conversation with an agent.
 */
export const ChatWithUs = ({ onUnavailableClick }) => {
  const safeLabel = useSafeLabel();
  const hasAgentConversationStarted = useRecoilValue(
    hasAgentConversationStartedState,
  );
  const { isGenesysReAuthenticating } = useIsGenesysReAuthenticating();
  const trackNotify = useInteractionTracking('notification');
  const trackButton = useInteractionTracking('button click');
  const { isOutOfHours } = useOpenningHoursEffect();
  const authenticationState = useAuthenticationState();
  const setHasUserInitiatedAuthChat = useSetHasUserInitiatedAuthChat();
  const setHasChatWithAgentBtnClicked = useSetHasChatWithAgentBtnClicked();

  const setGenesysError = useSetGenesysError();

  const {
    isStartingConversation,
    isEndingConversation,
    setConversationStarting,
    setConversationEnding,
    setResetConversationLoading,
  } = useMessagingServiceStatus();

  const isLoading = isStartingConversation || isEndingConversation;

  useEffect(() => {
    trackNotify({
      tealiumData: {
        event_content: 'chat with an agent',
      },
    })();
  }, [trackNotify]);

  const handleStartConversationFailed = useCallback(() => {
    setGenesysError(
      isLoggedInUser()
        ? DefinedError.AuthConversationInitiateError
        : DefinedError.UnAuthConversationInitiateError,
    );
    setResetConversationLoading();
  }, [setGenesysError, setResetConversationLoading]);

  const toggleConversation = () => {
    if (isOutOfHours && !hasAgentConversationStarted) {
      onUnavailableClick();

      return;
    }

    if (!hasAgentConversationStarted) {
      const handleFailingForOnce = once(handleStartConversationFailed);

      setHasChatWithAgentBtnClicked(true);

      setConversationStarting();

      waitForMessagingServiceStarted(30000).catch(handleFailingForOnce);

      if (isLoggedInUser()) {
        registerAuthPlugin();
        setHasUserInitiatedAuthChat(true);
        authenticationState === AuthState.authenticated &&
          publish(PubSubEvent.MessageServiceReady);
      } else {
        startAConversation({
          onFailure: handleFailingForOnce,
        });
      }
    } else {
      setHasChatWithAgentBtnClicked(false);
      setConversationEnding();
      setHasUserInitiatedAuthChat(false);
      clearConversation({ clearStorage: true });
    }
  };

  const getChatWithUsLabel = () => {
    if (!!isOutOfHours && !hasAgentConversationStarted) {
      return messages.chatUnavailable;
    }

    return !hasAgentConversationStarted
      ? messages.chatWithUs
      : messages.endConversation;
  };

  return (
    <Flex
      alignItems="center"
      flexWrap="nowrap"
      sx={{
        gap: rem(8),

        [oneColumnLayout]: {
          width: '100%',
          margin: `${rem(12)} 0 0`,
        },
      }}
    >
      <ChatWithAnAgentButton
        padding={rem(10)}
        icon="ContactLiveChat"
        onClick={trackButton({
          onClick: toggleConversation,
          tealiumData: {
            event_content: `${
              !hasAgentConversationStarted ? 'begin' : 'end'
            } messaging conversation`,
          },
        })}
        disabled={isLoading || isGenesysReAuthenticating}
      >
        <Typography color={white}>{safeLabel(getChatWithUsLabel())}</Typography>
      </ChatWithAnAgentButton>
      {isLoading && (
        <Spinner
          variant="light.small"
          label={safeLabel(
            isStartingConversation
              ? messages.startingConversation
              : messages.endingConversation,
          )}
          hideLabel
        />
      )}
    </Flex>
  );
};
